import { CtaBand } from "@/components/CtaBand";
import { PageHero } from "@/components/PageHero";
import { PillLink } from "@/components/PillLink";
import { Seo } from "@/components/Seo";
import { useReveals } from "@/lib/motion";
import { DOCS_URL } from "@/lib/site";

const SECURITY_EMAIL = "security@mineoptics.com";

// Only statements that can be checked against the code and the deployment
// belong here. Do not add certifications or audits that have not happened.
const PRACTICES = [
  {
    title: "Your organization is the boundary",
    body: "Projects, sites, drillholes, scenarios and quotes belong to an organization. The API checks the caller's membership and role on requests for that data.",
  },
  {
    title: "Roles decide who can change what",
    body: "Viewers read. Operators enter and edit data. Managers create and delete projects, sites and scenarios. Admins manage members and billing.",
  },
  {
    title: "Sign-in",
    body: "Accounts sign in with an email address and password. Passwords are stored as salted hashes by the authentication service, never in plain text, and sign-in attempts are rate limited.",
  },
  {
    title: "Encrypted in transit",
    body: "The website, the dashboard and the API are served over HTTPS.",
  },
  {
    title: "No card details here",
    body: "Paid plans are annual contracts, invoiced to the organization. An organization on an earlier monthly subscription manages it in the Stripe billing portal. Card details are not sent to or stored on MineOptics servers.",
  },
  {
    title: "Changes leave a record",
    body: "Every save, calculation, issued report and copy of a scenario is recorded with who did it and the values that changed. Members can read the record, and no member can edit or delete an entry. An issued report is frozen: later edits to its scenario do not reach it.",
  },
  {
    title: "Deletion you can undo",
    body: "Deleting a drillhole shows what is attached to it first, hides it rather than erasing it, and records who did it.",
  },
];

export default function Security() {
  const scope = useReveals();

  return (
    <div ref={scope}>
      <Seo
        title="Security"
        description="How MineOptics protects your data today: organization boundaries, roles, sign-in, encryption in transit, payments and a record of changes."
        path="/security"
      />
      <PageHero
        eyebrow="Security"
        title="Security, stated plainly."
        lede="What protects your data in MineOptics today. No badges we have not earned, and a direct line if you find a problem."
      />

      <section data-theme="light" className="bg-bg">
        <div className="shell py-24 md:py-36">
          <p data-fade className="eyebrow text-mute">
            In place today
          </p>
          <ol className="mt-8">
            {PRACTICES.map((practice, index) => (
              <li key={practice.title}>
                <span data-rule className="rule" />
                <div data-fade className="grid gap-4 py-9 md:grid-cols-[5rem_minmax(0,1fr)_minmax(0,1.2fr)] md:gap-8 md:py-12">
                  <span className="eyebrow pt-2 text-mute">{String(index + 1).padStart(2, "0")}</span>
                  <h2 className="subtitle uppercase">{practice.title}</h2>
                  <p className="lede max-w-[54ch] text-mute">{practice.body}</p>
                </div>
              </li>
            ))}
          </ol>
          <span data-rule className="rule" />
        </div>
      </section>

      <section data-theme="dark" className="bg-bg">
        <div className="shell grid gap-14 py-24 md:py-36 lg:grid-cols-2">
          <div>
            <p data-fade className="eyebrow eyebrow-mark mb-6 text-mute">
              Certifications
            </p>
            <h2 data-split className="subtitle max-w-[18ch] uppercase">
              Ask us where we stand.
            </h2>
            <p data-fade className="lede mt-6 max-w-[48ch] text-fg/75">
              We do not list compliance certifications on this page. If your procurement process needs a specific
              attestation, a questionnaire answered or a data processing agreement, write to us and you will get a
              direct answer.
            </p>
          </div>
          <div>
            <p data-fade className="eyebrow eyebrow-mark mb-6 text-mute">
              Report a vulnerability
            </p>
            <h2 data-split className="subtitle max-w-[18ch] uppercase">
              Found something? Tell us first.
            </h2>
            <p data-fade className="lede mt-6 max-w-[48ch] text-fg/75">
              Send the details and steps to reproduce to the address below. Please give us a reasonable chance to fix
              the issue before making it public, and do not access data that is not yours.
            </p>
            <div data-fade className="mt-8 flex flex-wrap gap-3">
              <PillLink to={`mailto:${SECURITY_EMAIL}`} label={SECURITY_EMAIL} variant="accent" />
              <PillLink to={`${DOCS_URL}/user-guide/team-collaboration`} label="How roles work" variant="outline" />
            </div>
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
